import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import {
  Globe,
  TrendUp,
  Users,
  Buildings,
  Handshake,
  MapPin,
  GenderIntersex,
  Briefcase,
  FilePdf,
  Gear,
  ShareNetwork,
  ArrowRight, Leaf} from '@phosphor-icons/react';
import { getImpactDashboard, getOddObjectives, saveOddObjectives, createSharedReport } from '../services/api';
import apiInstance from '../services/api';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  AreaChart, Area, PieChart, Pie, Cell
} from 'recharts';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { PageHero } from '../components/motion/PageHero';

const ODD_ICONS = {
  odd1: Users,
  odd5: GenderIntersex,
  odd8: Briefcase,
  odd10: TrendUp,
  odd11: MapPin,
  odd17: Handshake,
};

const TERRITORY_COLORS = ['#00AEEF', '#80C51F', '#EA3C3A', '#FF8E00', '#D5007D'];

export default function ImpactESSPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all');
  const [objectivesOpen, setObjectivesOpen] = useState(false);
  const [objectives, setObjectives] = useState({});
  const [tempObj, setTempObj] = useState({});

  useEffect(() => {
    fetchData();
  }, [period]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dashRes, objRes] = await Promise.all([
        getImpactDashboard(period),
        getOddObjectives(),
      ]);
      setData(dashRes.data);
      setObjectives(objRes.data.values || {});
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveObjectives = async () => {
    try {
      await saveOddObjectives(tempObj);
      setObjectives(tempObj);
      setObjectivesOpen(false);
      toast.success('Objectifs sauvegardes');
      fetchData();
    } catch {
      toast.error('Erreur');
    }
  };

  const handleExportPDF = async () => {
    try {
      toast.info('Generation du PDF...');
      const res = await apiInstance.get(`/impact-ess/pdf?period=${period}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `rapport_impact_odd_${period}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF telecharge !');
    } catch {
      toast.error('Erreur PDF');
    }
  };

  const handleShare = async () => {
    try {
      const res = await createSharedReport({
        title: 'Rapport d\'Impact ESS - Indicateurs ODD',
        territoire: null,
        period: period,
        expires_days: 60,
      });
      const link = `${window.location.origin}/rapport/${res.data.token}`;
      await navigator.clipboard.writeText(link);
      toast.success('Lien copie !');
    } catch {
      toast.error('Erreur');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-[#2D5A4C]" />
      </div>
    );
  }

  if (!data) return null;

  const { odd_indicators, summary, monthly_evolution, territory_breakdown } = data;
  const oddKeys = ['odd1', 'odd5', 'odd8', 'odd10', 'odd11', 'odd17'];

  const radarData = oddKeys.map(key => ({
    odd: `ODD ${odd_indicators[key].number}`,
    label: odd_indicators[key].label,
    progress: Math.min(odd_indicators[key].progress, 100),
  }));

  const ProgressGauge = ({ value, max, color, size = 72 }) => {
    const pct = Math.min((value / Math.max(max, 1)) * 100, 100);
    const radius = (size - 8) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (pct / 100) * circumference;
    return (
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#E8E6E1" strokeWidth="5" />
          <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={color} strokeWidth="5"
            strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
            className="transition-all duration-1000" />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-sm font-bold text-[#1A2421]">{Math.round(pct)}%</span>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="impact-ess-page">
      {/* Hero header — UX transversale v2 */}
      <PageHero
        title="Impact ESS"
        subtitle="Mesure des retombées sociales et environnementales"
        icon={<Leaf size={24} weight="duotone" className="text-white" />}
        gradient="from-[#80C51F] to-[#2D5A4C]"
        accents={['#00AEEF', '#FF8E00']}
      />

      {/* ODD Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="odd-cards">
        {oddKeys.map(key => {
          const ind = odd_indicators[key];
          const Icon = ODD_ICONS[key];
          return (
            <Card key={key} className="border-[#E8E6E1] shadow-sm overflow-hidden hover:-translate-y-0.5 transition-transform duration-200" data-testid={`odd-card-${key}`}>
              <div className="h-1.5" style={{ background: ind.color }} />
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge style={{ background: ind.color, color: 'white' }} className="text-[10px] font-bold px-2">
                        ODD {ind.number}
                      </Badge>
                      <span className="text-xs font-semibold text-[#1A2421] truncate">{ind.label}</span>
                    </div>
                    <p className="text-[10px] text-[#5E6D66] mb-3">{ind.description}</p>
                    
                    <div className="text-2xl font-bold text-[#1A2421]" style={{ fontFamily: 'Manrope' }}>
                      {key === 'odd5' || key === 'odd10' ? `${ind.value}%` : ind.value}
                    </div>
                    <p className="text-[10px] text-[#5E6D66] mt-0.5">
                      Objectif: {key === 'odd5' || key === 'odd10' ? `${ind.objective}%` : ind.objective}
                    </p>
                  </div>
                  <ProgressGauge value={ind.value} max={ind.objective} color={ind.color} />
                </div>

                {/* Details */}
                <div className="mt-3 pt-3 border-t border-[#E8E6E1] space-y-1">
                  {key === 'odd1' && (
                    <>
                      <div className="flex justify-between text-[10px]"><span className="text-[#5E6D66]">Beneficiaires</span><span className="font-semibold">{ind.details.beneficiaires}</span></div>
                      <div className="flex justify-between text-[10px]"><span className="text-[#5E6D66]">Porteurs de projet</span><span className="font-semibold">{ind.details.porteurs_projet}</span></div>
                      <div className="flex justify-between text-[10px]"><span className="text-[#5E6D66]">Dossiers valides</span><span className="font-semibold">{ind.details.dossiers_valides}</span></div>
                    </>
                  )}
                  {key === 'odd5' && (
                    <>
                      <div className="flex justify-between text-[10px]"><span className="text-[#5E6D66]">Femmes</span><span className="font-semibold">{ind.details.femmes}</span></div>
                      <div className="flex justify-between text-[10px]"><span className="text-[#5E6D66]">Hommes</span><span className="font-semibold">{ind.details.hommes}</span></div>
                    </>
                  )}
                  {key === 'odd8' && (
                    <>
                      <div className="flex justify-between text-[10px]"><span className="text-[#5E6D66]">Cooperatives</span><span className="font-semibold">{ind.details.cooperatives}</span></div>
                      <div className="flex justify-between text-[10px]"><span className="text-[#5E6D66]">Associations</span><span className="font-semibold">{ind.details.associations}</span></div>
                      <div className="flex justify-between text-[10px]"><span className="text-[#5E6D66]">Montant collecte</span><span className="font-semibold">{(ind.details.montant_collecte || 0).toLocaleString('fr-FR')} EUR</span></div>
                    </>
                  )}
                  {key === 'odd10' && (
                    <div className="flex justify-between text-[10px]"><span className="text-[#5E6D66]">Territoires couverts</span><span className="font-semibold">{ind.details.territoires_couverts}/{ind.details.total_territoires}</span></div>
                  )}
                  {key === 'odd11' && ind.details.territory_breakdown && (
                    <>
                      {ind.details.territory_breakdown.slice(0, 3).map(t => (
                        <div key={t.territoire} className="flex justify-between text-[10px]"><span className="text-[#5E6D66]">{t.territoire}</span><span className="font-semibold">{t.organizations} orgs</span></div>
                      ))}
                    </>
                  )}
                  {key === 'odd17' && (
                    <>
                      <div className="flex justify-between text-[10px]"><span className="text-[#5E6D66]">Partenaires actifs</span><span className="font-semibold">{ind.details.partenaires_actifs}</span></div>
                      <div className="flex justify-between text-[10px]"><span className="text-[#5E6D66]">Total contacts</span><span className="font-semibold">{ind.details.total_contacts}</span></div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Radar ODD */}
        <Card className="border-[#E8E6E1] shadow-sm" data-testid="odd-radar-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-[#1A2421] flex items-center gap-2">
              <Globe size={18} className="text-[#2D5A4C]" />
              Progression ODD
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData}>
                  <PolarGrid stroke="#E8E6E1" />
                  <PolarAngleAxis dataKey="odd" tick={{ fontSize: 10, fill: '#5E6D66' }} />
                  <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 9, fill: '#5E6D66' }} />
                  <Radar name="Progression" dataKey="progress" stroke="#2D5A4C" fill="#2D5A4C" fillOpacity={0.25} strokeWidth={2} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Territory distribution */}
        <Card className="border-[#E8E6E1] shadow-sm" data-testid="territory-distribution">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-[#1A2421] flex items-center gap-2">
              <MapPin size={18} className="text-[#2D5A4C]" />
              Repartition territoriale
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={territory_breakdown} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" />
                  <XAxis dataKey="territoire" tick={{ fontSize: 9, fill: '#5E6D66' }}
                    tickFormatter={(v) => v.length > 8 ? v.slice(0, 8) + '.' : v} />
                  <YAxis tick={{ fontSize: 10, fill: '#5E6D66' }} />
                  <Tooltip contentStyle={{ background: 'white', border: '1px solid #E8E6E1', borderRadius: '8px', fontSize: 11 }} />
                  <Bar dataKey="organizations" name="Organisations" fill="#00AEEF" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="contacts" name="Contacts" fill="#80C51F" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="dossiers" name="Dossiers" fill="#EA3C3A" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Evolution */}
      <Card className="border-[#E8E6E1] shadow-sm" data-testid="monthly-evolution">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold text-[#1A2421] flex items-center gap-2">
            <TrendUp size={18} className="text-[#2D5A4C]" />
            Evolution mensuelle
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[250px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={monthly_evolution} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" />
                <XAxis dataKey="month_short" tick={{ fontSize: 10, fill: '#5E6D66' }} />
                <YAxis tick={{ fontSize: 10, fill: '#5E6D66' }} />
                <Tooltip contentStyle={{ background: 'white', border: '1px solid #E8E6E1', borderRadius: '8px', fontSize: 11 }} />
                <Area type="monotone" dataKey="organizations" name="Organisations" stroke="#2D5A4C" fill="#2D5A4C" fillOpacity={0.15} strokeWidth={2} />
                <Area type="monotone" dataKey="contacts" name="Contacts" stroke="#D47A62" fill="#D47A62" fillOpacity={0.1} strokeWidth={2} />
                <Area type="monotone" dataKey="dossiers" name="Dossiers" stroke="#8DB0A1" fill="#8DB0A1" fillOpacity={0.1} strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="summary-kpis">
        <Card className="border-[#E8E6E1] shadow-sm">
          <CardContent className="p-4 text-center">
            <Buildings size={20} className="mx-auto text-[#2D5A4C] mb-1" />
            <p className="text-2xl font-bold text-[#1A2421]">{summary.total_organizations}</p>
            <p className="text-[10px] text-[#5E6D66] uppercase tracking-wider font-bold">Structures ESS</p>
          </CardContent>
        </Card>
        <Card className="border-[#E8E6E1] shadow-sm">
          <CardContent className="p-4 text-center">
            <Users size={20} className="mx-auto text-[#D47A62] mb-1" />
            <p className="text-2xl font-bold text-[#1A2421]">{summary.total_contacts}</p>
            <p className="text-[10px] text-[#5E6D66] uppercase tracking-wider font-bold">Beneficiaires</p>
          </CardContent>
        </Card>
        <Card className="border-[#E8E6E1] shadow-sm">
          <CardContent className="p-4 text-center">
            <MapPin size={20} className="mx-auto text-[#FF8E00] mb-1" />
            <p className="text-2xl font-bold text-[#1A2421]">{summary.territory_coverage}%</p>
            <p className="text-[10px] text-[#5E6D66] uppercase tracking-wider font-bold">Couverture terr.</p>
          </CardContent>
        </Card>
        <Card className="border-[#E8E6E1] shadow-sm">
          <CardContent className="p-4 text-center">
            <TrendUp size={20} className="mx-auto text-[#2D5A4C] mb-1" />
            <p className="text-2xl font-bold text-[#1A2421]">{summary.total_paid.toLocaleString('fr-FR')} EUR</p>
            <p className="text-[10px] text-[#5E6D66] uppercase tracking-wider font-bold">Montant collecte</p>
          </CardContent>
        </Card>
      </div>

      {/* Objectives Dialog */}
      <Dialog open={objectivesOpen} onOpenChange={setObjectivesOpen}>
        <DialogContent className="max-w-md" data-testid="objectives-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Gear size={20} className="text-[#2D5A4C]" />
              Configurer les objectifs ODD
            </DialogTitle>
            <DialogDescription className="sr-only">Contenu de la boite de dialogue</DialogDescription>
          </DialogHeader>
          <p className="text-xs text-[#5E6D66] mb-2">Definissez vos objectifs annuels pour chaque indicateur ODD.</p>
          <div className="space-y-3 max-h-[350px] overflow-y-auto">
            {oddKeys.map(key => {
              const ind = odd_indicators[key];
              return (
                <div key={key} className="flex items-center gap-3">
                  <Badge style={{ background: ind.color, color: 'white' }} className="text-[10px] w-14 justify-center flex-shrink-0">
                    ODD {ind.number}
                  </Badge>
                  <span className="text-xs text-[#1A2421] flex-1 min-w-0 truncate">{ind.label}</span>
                  <Input
                    type="number"
                    className="w-20"
                    value={tempObj[key] ?? ind.objective}
                    onChange={(e) => setTempObj(prev => ({ ...prev, [key]: parseInt(e.target.value) || 0 }))}
                    data-testid={`obj-input-${key}`}
                  />
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setObjectivesOpen(false)}>Annuler</Button>
            <Button onClick={handleSaveObjectives} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="save-objectives-btn">
              Sauvegarder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
